const keyBy = require('lodash/keyBy')
const { resolveManufacturerProductId } = require('./product-alias')

/**
 * Memoizing resolver for "which manufacturer-specific product does this batch
 * point at?". Wraps the pure resolution in product-alias.js and an injected
 * `fetchBatchesByIds(ids) -> { [batchId]: batchDoc }` so every runtime can share
 * the same logic while sourcing batch docs however it can (For now Couch master-data).
 *
 * The memo is per-resolver (per invocation), so batch docs are fetched once and
 * reused across all products/shipments in that run.
 */
const createBatchProductResolver = ({ fetchBatchesByIds }) => {
  if (typeof fetchBatchesByIds !== 'function') {
    throw new Error('createBatchProductResolver requires a fetchBatchesByIds function')
  }

  const cache = new Map() // batchId -> batch document | null

  const ensureBatches = async (batchIds) => {
    const missing = [...new Set(batchIds)].filter(batchId => batchId && !cache.has(batchId))
    if (!missing.length) return
    const fetched = (await fetchBatchesByIds(missing)) || {}
    // The fetch may return an array of docs (e.g. `listBatches`) or a map.
    const fetchedById = Array.isArray(fetched) ? keyBy(fetched, '_id') : fetched
    missing.forEach(batchId => cache.set(batchId, fetchedById[batchId] || null))
  }

  const genericProductIdOf = (batchId) => String(batchId).split(':manufacturer:')[0]

  const resolve = async ({ batchId, genericProductId, productsById } = {}) => {
    if (!batchId) return null
    await ensureBatches([batchId])
    return resolveManufacturerProductId({
      batchId,
      batch: cache.get(batchId),
      genericProductId: genericProductId || genericProductIdOf(batchId),
      productsById
    })
  }

  /**
   * Resolve many batches in one bulk fetch.
   * @returns {Promise<Object>} batchId -> manufacturer-specific product id | null
   */
  const resolveMany = async ({ batchIds, productsById } = {}) => {
    const ids = [...new Set((batchIds || []).filter(Boolean))]
    await ensureBatches(ids)
    return ids.reduce((acc, batchId) => {
      acc[batchId] = resolveManufacturerProductId({
        batchId,
        batch: cache.get(batchId),
        genericProductId: genericProductIdOf(batchId),
        productsById
      })
      return acc
    }, {})
  }

  return { resolve, resolveMany }
}

/**
 * Resolve many batch ids to their manufacturer-specific product SKU (the bare
 * code, e.g. `ghsc-aid0417h`) using the batch docs' alias. This is the input
 * the shipment volume query expects (`batchProductSkuById`), so callers that
 * already hold batch ids (or can read the shipment lines) prepare it here and
 * keep the volume calculation itself free of lookups.
 *
 * @param {string[]} batchIds
 * @param {(ids: string[]) => Promise<Object>} fetchBatchesByIds
 * @returns {Promise<Object>} batchId -> sku
 */
const resolveBatchProductSkusByBatchIds = async (batchIds, fetchBatchesByIds) => {
  const ids = [...new Set((batchIds || []).filter(Boolean))]
  if (!ids.length) return {}
  const resolver = createBatchProductResolver({ fetchBatchesByIds })
  const resolved = await resolver.resolveMany({ batchIds: ids })
  return Object.entries(resolved).reduce((acc, [batchId, productId]) => {
    if (productId) acc[batchId] = productId.replace(/^product:/, '')
    return acc
  }, {})
}

module.exports = { createBatchProductResolver, resolveBatchProductSkusByBatchIds }
